// Менюто на „Складова база" — ЕДИНСТВЕНОТО място, което казва кои страници има,
// в коя секция са и с какви значки. Sidebar-ът го рисува, а Inventory.tsx
// проверява срещу него, че всеки slug има страница.
//
// Един модул = един ред: най-горе „Табло" (началната страница — какво чака и
// откъде да започнеш), под него ДОКУМЕНТИТЕ (текущата работа и историята им на
// една страница, в табове — `DocumentTabs`), СПРАВКИТЕ (преглед, филтри, печат)
// и НАСТРОЙКИТЕ. Архивът на документ не е отделен ред — той е табът „Архив" на
// страницата му; неплатените доставки са табът „Неплатени" на „Доставки".
// Старите адреси (`/inventory/deliveries`, `/inventory/delivery-payments`…)
// пренасочват към таба — `LEGACY_SLUG_TARGETS`.
import type { ComponentType } from "react";
import {
  AppstoreOutlined,
  ContainerOutlined,
  DashboardOutlined,
  DeleteOutlined,
  ExportOutlined,
  FileDoneOutlined,
  InboxOutlined,
  InteractionOutlined,
  ReconciliationOutlined,
  RetweetOutlined,
  SettingOutlined,
  ShoppingOutlined,
  SwapOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import type { SignalTone } from "../list/signals";

// Броячите, от които се смятат значките (виж useSidebarBadges). Черновите
// (`*Drafts`) са незавършените листове на ресторанта — мои и на колегите.
// `overduePendingReports` — колко от чакащите отчети са закъснели;
// `staleStocktakeDrafts` — колко от незавършените инвентаризации са пипани
// вчера или по-рано (BE смята системното количество при запис, затова
// продажбите между броенето и записа стават липса); `heldDeliveryDrafts` —
// задържаните доставки; `shortageProducts` — продуктите с излишък от продажби
// (само ако таблото или справката вече са го заредили — менюто не тегли
// тежката заявка).
export type NavCountKey =
  | "pendingReports"
  | "overduePendingReports"
  | "deliveryDrafts"
  | "heldDeliveryDrafts"
  | "unpaidDeliveries"
  | "writeoffDrafts"
  | "stocktakeDrafts"
  | "staleStocktakeDrafts"
  | "transferDrafts"
  | "shortageProducts";

export type NavBadgeCounts = Partial<Record<NavCountKey, number>>;

// Чия значка носи редът
export type InventoryNavBadge = "dashboard" | "posting" | "delivery" | "writeoff" | "stocktake" | "transfer";

export type InventoryNavItem = {
  slug: string;
  labelKey: string;
  Icon: ComponentType<{ className?: string }>;
  badge?: InventoryNavBadge;
};

export type InventoryNavSection = {
  // Без заглавие — първата секция („Табло" стои сам най-горе)
  titleKey?: string;
  items: InventoryNavItem[];
};

export const DASHBOARD_SLUG = "dashboard";

// Документите — в реда на работата: отчетите на деня, стоката, която влиза,
// излиза, се брои и се мести
export const DOCUMENT_NAV_ITEMS: InventoryNavItem[] = [
  { slug: "posting", labelKey: "inventoryPage.posting", Icon: FileDoneOutlined, badge: "posting" },
  { slug: "delivery", labelKey: "inventoryPage.navDeliveries", Icon: InboxOutlined, badge: "delivery" },
  { slug: "writeoff", labelKey: "inventoryPage.navWriteoffs", Icon: ExportOutlined, badge: "writeoff" },
  {
    slug: "stocktake",
    labelKey: "inventoryPage.navStocktakes",
    Icon: ReconciliationOutlined,
    badge: "stocktake",
  },
  { slug: "transfer", labelKey: "inventoryPage.navTransfers", Icon: RetweetOutlined, badge: "transfer" },
];

export const INVENTORY_NAV: InventoryNavSection[] = [
  {
    items: [
      { slug: DASHBOARD_SLUG, labelKey: "inventoryPage.dashNav", Icon: DashboardOutlined, badge: "dashboard" },
    ],
  },
  { titleKey: "inventoryPage.sectionDocuments", items: DOCUMENT_NAV_ITEMS },
  {
    titleKey: "inventoryPage.sectionReports",
    items: [
      { slug: "stock", labelKey: "inventoryPage.stockReport", Icon: AppstoreOutlined },
      { slug: "sold-products", labelKey: "inventoryPage.soldProducts", Icon: ShoppingOutlined },
      { slug: "sold-recipes", labelKey: "inventoryPage.soldRecipes", Icon: ContainerOutlined },
      { slug: "shortage", labelKey: "inventoryPage.negativeReport", Icon: WarningOutlined },
      { slug: "substitutions", labelKey: "inventoryPage.substitutionsReport", Icon: InteractionOutlined },
      { slug: "deleted", labelKey: "inventoryPage.deletedReport", Icon: DeleteOutlined },
    ],
  },
  {
    titleKey: "inventoryPage.sectionConfig",
    items: [
      { slug: "interchangeables", labelKey: "inventoryPage.interchange", Icon: SwapOutlined },
      { slug: "settings", labelKey: "inventoryPage.settings", Icon: SettingOutlined },
    ],
  },
];

// Началната страница на модула: таблото — какво чака и откъде да започнеш.
export const DEFAULT_INVENTORY_SLUG = DASHBOARD_SLUG;

export const INVENTORY_NAV_SLUGS = INVENTORY_NAV.flatMap((section) => section.items.map((item) => item.slug));

// ─── Табовете на документите ──────────────────────────────────────────────
// Табът живее в адреса (`?tab=archive`), за да работят линковете и „назад";
// първият таб на страницата е без параметър.
export const DOCUMENT_TAB_PARAM = "tab";
export type DocumentTabKey = "current" | "unpaid" | "archive";

// Адресът на таб от страница на документ: `documentTabPath("delivery", "unpaid")`
// → `/inventory/delivery?tab=unpaid`; текущият таб е голият адрес.
export const documentTabPath = (slug: string, tab: DocumentTabKey = "current") =>
  tab === "current" ? `/inventory/${slug}` : `/inventory/${slug}?${DOCUMENT_TAB_PARAM}=${tab}`;

// Старите редове от менюто → табът, в който живеят сега. Inventory.tsx
// пренасочва (replace, със същия router state — напр. `{savedDocument}`).
export const LEGACY_SLUG_TARGETS: Record<string, string> = {
  "delivery-payments": documentTabPath("delivery", "unpaid"),
  "posted-reports": documentTabPath("posting", "archive"),
  deliveries: documentTabPath("delivery", "archive"),
  writeoffs: documentTabPath("writeoff", "archive"),
  stocktakes: documentTabPath("stocktake", "archive"),
  transfers: documentTabPath("transfer", "archive"),
};

// ─── Значките ─────────────────────────────────────────────────────────────
// Червена цифра САМО за закъсняло/блокиращо (закъснял отчет, инвентаризация от
// вчера или по-стара); всичко чакащо — сив текст („1 чернова", „2 неплатени",
// „3 за разнасяне"). „Табло" — червена цифра = броят БЛОКИРАЩИ редове в
// опашката му (`dashboardTasks`: закъснял отчет и закъсняла инвентаризация —
// по ред на всеки, задържана доставка — по ред, излишъкът — един ред) или нищо.
// `hints` са i18n ключове с брой (плурализират се по `count`).
export type NavBadgeHint = { key: string; count: number };
export type NavBadgeView = {
  // колко закъсняват/блокират (0 — без червена цифра) и защо (i18n ключ)
  critical: number;
  criticalKey: string | null;
  hints: NavBadgeHint[];
};

const hintOf = (key: string, count: number | undefined): NavBadgeHint[] =>
  count && count > 0 ? [{ key, count }] : [];

const DRAFTS_HINT = "inventoryPage.navHintDrafts";

export const navBadgeViewOf = (badge: InventoryNavBadge, counts: NavBadgeCounts): NavBadgeView | null => {
  const overdue = counts.overduePendingReports ?? 0;
  const staleStocktakes = counts.staleStocktakeDrafts ?? 0;
  let view: NavBadgeView;
  switch (badge) {
    case "dashboard":
      view = {
        critical:
          overdue + staleStocktakes + (counts.heldDeliveryDrafts ?? 0) + ((counts.shortageProducts ?? 0) > 0 ? 1 : 0),
        criticalKey: "inventoryPage.navCriticalTotal",
        hints: [],
      };
      break;
    case "posting":
      view = {
        critical: overdue,
        criticalKey: "inventoryPage.navCriticalReports",
        hints: hintOf("inventoryPage.navHintToPost", counts.pendingReports),
      };
      break;
    case "delivery":
      view = {
        critical: 0,
        criticalKey: null,
        hints: [
          ...hintOf(DRAFTS_HINT, counts.deliveryDrafts),
          ...hintOf("inventoryPage.navHintUnpaid", counts.unpaidDeliveries),
        ],
      };
      break;
    case "stocktake":
      view = {
        critical: staleStocktakes,
        criticalKey: "inventoryPage.navCriticalStocktakes",
        hints: hintOf(DRAFTS_HINT, counts.stocktakeDrafts),
      };
      break;
    case "writeoff":
      view = { critical: 0, criticalKey: null, hints: hintOf(DRAFTS_HINT, counts.writeoffDrafts) };
      break;
    case "transfer":
      view = { critical: 0, criticalKey: null, hints: hintOf(DRAFTS_HINT, counts.transferDrafts) };
      break;
  }
  return view.critical > 0 || view.hints.length ? view : null;
};

// Тонът на значката (точката в свитото меню): червено при закъсняло, иначе
// неутрално сиво; без нищо — без тон.
export const navBadgeToneOf = (badge: InventoryNavBadge, counts: NavBadgeCounts): SignalTone | null => {
  const view = navBadgeViewOf(badge, counts);
  if (!view) return null;
  return view.critical > 0 ? "critical" : "neutral";
};
